/**
 * The filter pump's state, as the spa reports it on the live topic's `Filter`
 * field: the same eight values the old apps labelled. The one table for the
 * Filtration page, the Spa Details grid and the reports.
 */
export type FilterTone = 'idle' | 'active' | 'warm' | 'alert';

export interface FilterStateInfo {
  label: string;
  tone: FilterTone;
  /** Anything but idle or suspended: a cycle is already under way. */
  running: boolean;
  /** A manual cycle can only start from idle or suspended, as in the old apps. */
  canStart: boolean;
}

export const FILTER_STATES: { [state: number]: { label: string; tone: FilterTone } } = {
  0: { label: 'Idle', tone: 'idle' },
  1: { label: 'Purging', tone: 'active' },
  2: { label: 'Filtering', tone: 'active' },
  3: { label: 'Suspended', tone: 'idle' },
  4: { label: 'Over temperature', tone: 'alert' },
  5: { label: 'Resuming', tone: 'active' },
  6: { label: 'Boost', tone: 'warm' },
  7: { label: 'Sanitizing', tone: 'active' },
};

/** State number -> label, for the grid and report cells that index by number. */
export const filterStateLabels: { [state: number]: string } = Object.fromEntries(
  Object.entries(FILTER_STATES).map(([state, { label }]) => [Number(state), label]),
);

export function filterStateInfo(state: unknown): FilterStateInfo {
  const n = typeof state === 'number' ? state : Number(state);
  const entry = state === undefined || state === null || Number.isNaN(n) ? undefined : FILTER_STATES[n];
  if (!entry) {
    return {
      label: state === undefined || state === null ? '—' : `State ${state}`,
      tone: 'idle',
      running: false,
      canStart: false,
    };
  }
  const canStart = n === 0 || n === 3;
  return { ...entry, running: !canStart, canStart };
}
